import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Shield, Building2, CheckCircle2, XCircle, Clock, 
  Eye, FileText, ChevronRight, AlertTriangle, User,
  Mail, Phone, MapPin, Calendar, LogOut, Activity,
  RefreshCw, Search, TrendingUp, Users, BarChart3,
  AlertCircle, Zap, Lock, Unlock, RotateCcw, Ban,
  PlayCircle, PauseCircle, ChevronDown, ChevronUp,
  Home, Bell, ArrowLeft, ChevronLeft
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import AEDIcon from '@/components/icons/AEDIcon';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import api from '@/services/api';
import DeadlinesMonitor from '@/components/DeadlinesMonitor';
import RiskDashboard from '@/components/RiskDashboard';
import OffersPanel from '@/components/OffersPanel';
import ClassificationsPanel from '@/components/ClassificationsPanel';
import PaymentsPanel from '@/components/PaymentsPanel';
import AmendmentsPanel from '@/components/AmendmentsPanel';
import DealTimelineViewer from '@/components/DealTimelineViewer';
import AgentsPanel from '@/components/AgentsPanel';
import KYCManagementPanel from '@/components/KYCManagementPanel';
import ARSAdminPanel from '@/components/ARSAdminPanel';
import BSSAdminPanel from '@/components/BSSAdminPanel';
import TWMAdminPanel from '@/components/TWMAdminPanel';
import CommissionAdminPanel from '@/components/CommissionAdminPanel';
import PredictiveIntelligencePanel from '@/components/PredictiveIntelligencePanel';
import CommitmentAdminPanel from '@/components/CommitmentAdminPanel';
import AmendmentIntelligencePanel from '@/components/AmendmentIntelligencePanel';
import ValidationPanel from '@/components/ValidationPanel';
import ContractAdminPanel from '@/components/ContractAdminPanel';

// ============ CONSTANTS ============

const SEVERITY_COLORS = {
  INFO: 'bg-blue-600/20 text-blue-400 border-blue-600/30',
  WARNING: 'bg-gold-600/20 text-gold-400 border-gold-600/30',
  CRITICAL: 'bg-red-600/20 text-red-400 border-red-600/30'
};

const SEVERITY_ICONS = {
  INFO: Activity,
  WARNING: AlertTriangle,
  CRITICAL: AlertCircle
};

const EVENT_CATEGORIES = {
  USER: { color: 'text-purple-400', icon: User },
  PROPERTY: { color: 'text-blue-400', icon: Building2 },
  VIEWING: { color: 'text-green-400', icon: Eye },
  OFFER: { color: 'text-gold-400', icon: TrendingUp },
  ADMIN: { color: 'text-red-400', icon: Shield },
  SYSTEM: { color: 'text-gray-400', icon: Zap }
};

const formatTimestamp = (ts) => {
  if (!ts) return 'N/A';
  const date = new Date(ts);
  const now = new Date();
  const diff = now - date;
  
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  
  return date.toLocaleDateString('en-AE', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getEventCategory = (eventType) => {
  if (!eventType) return 'SYSTEM';
  if (eventType.startsWith('USER_') || eventType.startsWith('SELLER_')) return 'USER';
  if (eventType.startsWith('PROPERTY_')) return 'PROPERTY';
  if (eventType.startsWith('VIEWING_')) return 'VIEWING';
  if (eventType.startsWith('OFFER_') || eventType.startsWith('NEGOTIATION_')) return 'OFFER';
  if (eventType.startsWith('ADMIN_')) return 'ADMIN';
  return 'SYSTEM';
};

// ============ COMPONENTS ============

const MetricCard = ({ title, value, subtitle, icon: Icon, color = 'primary' }) => {
  const colorClasses = {
    primary: 'bg-primary/10 border-primary/30 text-primary',
    green: 'bg-green-950/20 border-green-600/30 text-green-400',
    yellow: 'bg-gold-950/20 border-gold-600/30 text-gold-400',
    gold: 'bg-gold-950/20 border-gold-600/30 text-gold-400',
    blue: 'bg-blue-950/20 border-blue-600/30 text-blue-400',
    purple: 'bg-purple-950/20 border-purple-600/30 text-purple-400',
    red: 'bg-red-950/20 border-red-600/30 text-red-400'
  };

  return (
    <Card className={`${colorClasses[color]} border`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest opacity-70">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-xs opacity-50 mt-1">{subtitle}</p>}
          </div>
          {Icon && <Icon className="w-8 h-8 opacity-30" />}
        </div>
      </CardContent>
    </Card>
  );
};

const ActivityLogItem = ({ log, onClick }) => {
  const category = getEventCategory(log.event_type);
  const CategoryIcon = EVENT_CATEGORIES[category]?.icon || Activity;
  const SeverityIcon = SEVERITY_ICONS[log.severity] || Activity;
  
  return (
    <div 
      className="p-3 rounded-lg border border-white/5 hover:border-white/20 bg-white/5 hover:bg-white/10 transition-all cursor-pointer group"
      onClick={() => onClick?.(log)}
      data-testid={`activity-log-${log.timestamp}`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${SEVERITY_COLORS[log.severity] || SEVERITY_COLORS.INFO}`}>
          <SeverityIcon className="w-4 h-4" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium ${EVENT_CATEGORIES[category]?.color || 'text-gray-400'}`}>
              {log.event_type?.replace(/_/g, ' ')}
            </span>
            <Badge variant="outline" className="text-[10px] border-white/20">
              {log.entity?.type}
            </Badge>
          </div>
          
          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
            <span>{log.actor?.email || 'System'}</span>
            <span>•</span>
            <span>{formatTimestamp(log.timestamp)}</span>
          </div>
          
          {log.state_transition?.from && log.state_transition?.to && (
            <div className="flex items-center gap-2 mt-2 text-xs">
              <Badge variant="outline" className="border-red-600/30 text-red-400">
                {log.state_transition.from}
              </Badge>
              <ChevronRight className="w-3 h-3 text-muted-foreground" />
              <Badge variant="outline" className="border-green-600/30 text-green-400">
                {log.state_transition.to}
              </Badge>
            </div>
          )}
          
          {log.metadata?.reason && (
            <p className="text-xs text-muted-foreground mt-2 italic">
              "{log.metadata.reason}"
            </p>
          )}
        </div>
        
        <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </div>
  );
};

const AlertItem = ({ alert }) => {
  const SeverityIcon = SEVERITY_ICONS[alert.severity] || AlertTriangle;
  
  return (
    <div className={`p-3 rounded-lg border ${SEVERITY_COLORS[alert.severity]}`}>
      <div className="flex items-start gap-3">
        <SeverityIcon className="w-5 h-5 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium">{alert.event_type?.replace(/_/g, ' ')}</p>
          <p className="text-xs opacity-70 mt-1">{alert.metadata?.reason}</p>
          <p className="text-xs opacity-50 mt-1">{formatTimestamp(alert.timestamp)}</p>
        </div>
      </div>
    </div>
  );
};

// ============ MAIN COMPONENT ============

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const token = localStorage.getItem('rashii_access_token');
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [pendingProperties, setPendingProperties] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Control Panel State
  const [controlDialog, setControlDialog] = useState({ open: false, type: null, data: null });
  const [controlReason, setControlReason] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Property Review State
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, type: null });
  const [actionReason, setActionReason] = useState('');
  
  // User Management State
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailDialog, setUserDetailDialog] = useState(false);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const [metricsRes, activityRes, alertsRes, pendingRes, usersRes] = await Promise.all([
        api.get('/admin/metrics'),
        api.get('/admin/activity?limit=50'),
        api.get('/admin/alerts?limit=30'),
        api.get('/admin/properties/pending'),
        api.get('/admin/users?limit=100')
      ]);
      
      setMetrics(metricsRes.data);
      setActivityLogs(activityRes.data.logs || []);
      setAlerts(alertsRes.data.alerts || []);
      setPendingProperties(pendingRes.data.properties || []);
      setAllUsers(usersRes.data || []);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load command center data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Access denied. Admin only.');
      navigate('/dashboard');
      return;
    }
    fetchData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [user, navigate, fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleRecomputeAlerts = async () => {
    try {
      await api.post('/admin/alerts/recompute');
      await fetchData();
      toast.success('Risk evaluation complete');
    } catch (error) {
      toast.error('Failed to recompute alerts');
    }
  };

  // Property Actions
  const handlePropertyAction = async () => {
    if (!selectedProperty) return;
    
    setProcessing(true);
    try {
      await api.post(`/admin/properties/${selectedProperty.id}/verify`, {
        action: actionDialog.type,
        reason: actionReason || (actionDialog.type === 'approve' 
          ? 'Property verified and approved' 
          : 'Property requires changes')
      });

      toast.success(actionDialog.type === 'approve' 
        ? 'Property approved! Seller notified.' 
        : 'Property rejected. Seller notified.');
      
      setActionDialog({ open: false, type: null });
      setSelectedProperty(null);
      setActionReason('');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setProcessing(false);
    }
  };

  // Control Panel Actions
  const handleControlAction = async () => {
    if (!controlReason || controlReason.length < 5) {
      toast.error('Please provide a valid reason (min 5 characters)');
      return;
    }

    setProcessing(true);
    try {
      let endpoint = '';
      let payload = { reason: controlReason };

      switch (controlDialog.type) {
        case 'reset-slots':
          endpoint = '/admin/controls/reset-slots';
          payload = { property_id: controlDialog.data?.id, reason: controlReason, new_slot_count: 9 };
          break;
        case 'freeze':
          endpoint = '/admin/controls/freeze-listing';
          payload = { property_id: controlDialog.data?.id, reason: controlReason, freeze: true };
          break;
        case 'unfreeze':
          endpoint = '/admin/controls/freeze-listing';
          payload = { property_id: controlDialog.data?.id, reason: controlReason, freeze: false };
          break;
        case 'suspend-user':
          endpoint = '/admin/controls/suspend-user';
          payload = { user_id: controlDialog.data?.id, reason: controlReason, suspend: true };
          break;
        default:
          throw new Error('Unknown control action');
      }

      await api.post(endpoint, payload);
      toast.success('Action completed successfully');
      setControlDialog({ open: false, type: null, data: null });
      setControlReason('');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <Shield className="w-12 h-12 text-primary" />
          <span className="text-muted-foreground">Loading Command Center...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-command-center">
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-background/80 backdrop-blur-xl">
        <div className="container mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Back Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="text-muted-foreground hover:text-foreground"
                data-testid="back-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              
              {/* Home Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="text-muted-foreground hover:text-foreground"
                data-testid="home-btn"
              >
                <Home className="w-4 h-4 mr-1" />
                Home
              </Button>
              
              {/* Rashii Logo - Consistent sizing */}
              <div className="flex items-center gap-2 ml-4 cursor-pointer group" onClick={() => navigate('/')}>
                <div className="w-10 h-10 rounded logo-icon-metallic flex items-center justify-center">
                  <span className="font-display text-lg text-primary-foreground font-bold">R</span>
                </div>
                <span className="font-display text-xl font-medium tracking-tight gold-interactive">Rashii</span>
                <Badge className="ml-2 bg-red-600/20 text-red-400 border-red-600/30">
                  ADMIN
                </Badge>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Notifications Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => toast.info('Notifications will be available in the next update')}
                className="relative"
                data-testid="notifications-btn"
              >
                <Bell className="w-5 h-5" />
                {alerts.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[10px] flex items-center justify-center text-white">
                    {alerts.length > 9 ? '9+' : alerts.length}
                  </span>
                )}
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRefresh}
                disabled={refreshing}
                className="border-white/20"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <span className="text-sm text-muted-foreground">{user?.email}</span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-muted-foreground hover:text-foreground"
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-6">
        <div className="grid grid-cols-12 gap-6">
          
          {/* Main Content - Left Side */}
          <div className="col-span-8 space-y-6">
            
            {/* System Metrics Overview */}
            <div className="grid grid-cols-4 gap-4" data-testid="metrics-overview">
              <MetricCard 
                title="Active Sellers" 
                value={metrics?.active_sellers || 0} 
                icon={Users}
                color="purple"
              />
              <MetricCard 
                title="Active Properties" 
                value={metrics?.active_properties || 0} 
                icon={Building2}
                color="blue"
              />
              <MetricCard 
                title="In Negotiation" 
                value={metrics?.properties_in_negotiation || 0} 
                icon={TrendingUp}
                color="yellow"
              />
              <MetricCard 
                title="Conversion Rate" 
                value={`${metrics?.conversion_rate || 0}%`} 
                icon={BarChart3}
                color="green"
              />
            </div>

            {/* Secondary Metrics */}
            <div className="grid grid-cols-4 gap-4">
              <MetricCard 
                title="Pending Review" 
                value={metrics?.properties_pending || 0} 
                subtitle="Awaiting approval"
                color="yellow"
              />
              <MetricCard 
                title="Closed Deals" 
                value={metrics?.properties_closed || 0} 
                subtitle="Completed"
                color="green"
              />
              <MetricCard 
                title="Avg Below Ask" 
                value={`${metrics?.avg_offer_below_ask || 0}%`} 
                subtitle="Offer discount"
                color="blue"
              />
              <MetricCard 
                title="Slot Utilization" 
                value={`${metrics?.slot_utilization || 0}%`} 
                subtitle="Viewing slots used"
                color="purple"
              />
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-white/5 border border-white/10 flex-wrap">
                <TabsTrigger value="overview">Properties</TabsTrigger>
                <TabsTrigger value="kyc">KYC</TabsTrigger>
                <TabsTrigger value="agents">Agents</TabsTrigger>
                <TabsTrigger value="ars" className="text-primary">ARS</TabsTrigger>
                <TabsTrigger value="bss" className="text-purple-400">BSS</TabsTrigger>
                <TabsTrigger value="twm" className="text-cyan-400">TWM</TabsTrigger>
                <TabsTrigger value="commission" className="text-emerald-400">Commission</TabsTrigger>
                <TabsTrigger value="commitment" className="text-amber-400">Commitment</TabsTrigger>
                <TabsTrigger value="predictions" className="text-violet-400">AI Predictions</TabsTrigger>
                <TabsTrigger value="amendment-ai" className="text-rose-400">Amendment AI</TabsTrigger>
                <TabsTrigger value="validation" className="text-emerald-400">Validation</TabsTrigger>
                <TabsTrigger value="contracts" className="text-teal-400">Contracts</TabsTrigger>
                <TabsTrigger value="classifications">Classifications</TabsTrigger>
                <TabsTrigger value="payments">Payments</TabsTrigger>
                <TabsTrigger value="amendments">Amendments</TabsTrigger>
                <TabsTrigger value="timeline">Timeline</TabsTrigger>
                <TabsTrigger value="deadlines">Deadlines</TabsTrigger>
                <TabsTrigger value="offers">Offers</TabsTrigger>
                <TabsTrigger value="users">Users</TabsTrigger>
                <TabsTrigger value="controls">Controls</TabsTrigger>
                <TabsTrigger value="alerts">Risk</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-4">
                <Card className="border-white/10 bg-card/50">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Clock className="w-5 h-5 text-gold-400" />
                        Properties Pending Verification
                      </CardTitle>
                      <Badge variant="outline" className="border-gold-600/50 text-gold-400">
                        {pendingProperties.length} pending
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {pendingProperties.length === 0 ? (
                      <div className="text-center py-8">
                        <CheckCircle2 className="w-10 h-10 text-green-400/50 mx-auto mb-3" />
                        <p className="text-muted-foreground">No properties pending verification</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {pendingProperties.map((prop) => (
                          <div
                            key={prop.id}
                            className="p-4 rounded-lg border border-white/10 hover:border-primary/30 bg-white/5 hover:bg-white/10 transition-all cursor-pointer"
                            onClick={() => setSelectedProperty(prop)}
                            data-testid={`pending-property-${prop.id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-medium">{prop.title || 'Untitled Property'}</h4>
                                <p className="text-sm text-muted-foreground mt-1">
                                  {prop.location?.community || 'Location not specified'} • {prop.property_type}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  Seller: {prop.seller_info?.email || 'Unknown'}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-green-600/50 text-green-400 hover:bg-green-950/30"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedProperty(prop);
                                    setActionDialog({ open: true, type: 'approve' });
                                  }}
                                  data-testid={`approve-btn-${prop.id}`}
                                >
                                  <CheckCircle2 className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-red-600/50 text-red-400 hover:bg-red-950/30"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedProperty(prop);
                                    setActionDialog({ open: true, type: 'reject' });
                                  }}
                                  data-testid={`reject-btn-${prop.id}`}
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Reject
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Agents Tab */}
              <TabsContent value="agents" className="mt-4" data-testid="agents-tab">
                <Card className="border-white/10 bg-card/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="w-5 h-5 text-gold-400" />
                      Agent Management
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <AgentsPanel />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Agent Risk Scoring Tab */}
              <TabsContent value="ars" className="mt-4" data-testid="ars-tab">
                <ARSAdminPanel token={token} />
              </TabsContent>

              {/* Buyer Seriousness Scoring Tab */}
              <TabsContent value="bss" className="mt-4" data-testid="bss-tab">
                <BSSAdminPanel token={token} />
              </TabsContent>

              {/* Trust Weight Multiplier Tab */}
              <TabsContent value="twm" className="mt-4" data-testid="twm-tab">
                <TWMAdminPanel token={token} />
              </TabsContent>

              {/* Commission Automation Tab */}
              <TabsContent value="commission" className="mt-4" data-testid="commission-tab">
                <CommissionAdminPanel token={token} />
              </TabsContent>

              {/* Risk-Adjusted Commitment Tab */}
              <TabsContent value="commitment" className="mt-4" data-testid="commitment-tab">
                <CommitmentAdminPanel token={token} />
              </TabsContent>

              {/* Predictive Intelligence Tab */}
              <TabsContent value="predictions" className="mt-4" data-testid="predictions-tab">
                <PredictiveIntelligencePanel token={token} />
              </TabsContent>

              {/* Amendment Intelligence Tab */}
              <TabsContent value="amendment-ai" className="mt-4" data-testid="amendment-ai-tab">
                <AmendmentIntelligencePanel token={token} />
              </TabsContent>

              {/* Outcome Validation Tab */}
              <TabsContent value="validation" className="mt-4" data-testid="validation-tab">
                <ValidationPanel token={token} />
              </TabsContent>

              {/* Contract Intelligence Tab */}
              <TabsContent value="contracts" className="mt-4" data-testid="contracts-tab">
                <ContractAdminPanel />
              </TabsContent>

              {/* KYC Management Tab */}
              <TabsContent value="kyc" className="mt-4" data-testid="kyc-tab">
                <Card className="border-white/10 bg-card/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="w-5 h-5 text-gold-400" />
                      KYC Management
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <KYCManagementPanel />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Classifications Tab */}
              <TabsContent value="classifications" className="mt-4" data-testid="classifications-tab">
                <Card className="border-white/10 bg-card/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-gold-400" />
                      Condition Classifications Queue
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ClassificationsPanel />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Deadlines Governance Tab */}
              <TabsContent value="deadlines" className="mt-4">
                <Card className="border-white/10 bg-card/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-400" />
                      Deadline Governance Engine
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <DeadlinesMonitor />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Offers Tab */}
              <TabsContent value="offers" className="mt-4">
                <Card className="border-white/10 bg-card/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AEDIcon className="w-5 h-5 text-green-400" />
                      Platform Offers Overview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <OffersPanel isAdmin={true} />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Payments Tab */}
              <TabsContent value="payments" className="mt-4" data-testid="payments-tab">
                <Card className="border-white/10 bg-card/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AEDIcon className="w-5 h-5 text-green-400" />
                      Payment Intelligence
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <PaymentsPanel />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Amendments Tab */}
              <TabsContent value="amendments" className="mt-4" data-testid="amendments-tab">
                <Card className="border-white/10 bg-card/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="w-5 h-5 text-purple-400" />
                      Contract Amendments
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <AmendmentsPanel />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Deal Timeline Tab */}
              <TabsContent value="timeline" className="mt-4" data-testid="timeline-tab">
                <Card className="border-white/10 bg-card/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-blue-400" />
                      Deal Timeline Viewer
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <DealTimelineViewer />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Users Tab */}
              <TabsContent value="users" className="mt-4">
                <Card className="border-white/10 bg-card/50">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Users className="w-5 h-5 text-purple-400" />
                        All Platform Users
                      </CardTitle>
                      <Badge variant="outline" className="border-purple-600/50 text-purple-400">
                        {allUsers.length} users
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {allUsers.length === 0 ? (
                      <div className="text-center py-8">
                        <Users className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
                        <p className="text-muted-foreground">No users found</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {allUsers.map((u) => (
                          <div
                            key={u.id}
                            className="p-4 rounded-lg border border-white/10 hover:border-primary/30 bg-white/5 hover:bg-white/10 transition-all cursor-pointer"
                            onClick={() => {
                              setSelectedUser(u);
                              setUserDetailDialog(true);
                            }}
                            data-testid={`user-${u.id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                                  <User className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                  <p className="font-medium">{u.full_name || u.username || 'Unknown'}</p>
                                  <p className="text-sm text-muted-foreground">{u.email}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className={
                                  u.role === 'admin' ? 'bg-red-600/20 text-red-400 border-red-600/30' :
                                  u.role === 'seller' ? 'bg-blue-600/20 text-blue-400 border-blue-600/30' :
                                  'bg-green-600/20 text-green-400 border-green-600/30'
                                }>
                                  {u.role}
                                </Badge>
                                <Badge className={
                                  u.kyc_status === 'verified' ? 'bg-green-600/20 text-green-400' :
                                  'bg-gold-600/20 text-gold-400'
                                }>
                                  {u.kyc_status || 'pending'}
                                </Badge>
                                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="controls" className="mt-4">
                <Card className="border-white/10 bg-card/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Lock className="w-5 h-5 text-red-400" />
                      Manual Control Panel
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      All actions require a reason and are permanently logged
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <Button
                        variant="outline"
                        className="h-auto py-4 flex-col items-start border-white/20 hover:border-primary/50"
                        onClick={() => setControlDialog({ open: true, type: 'reset-slots', data: null })}
                        data-testid="control-reset-slots"
                      >
                        <RotateCcw className="w-5 h-5 mb-2 text-blue-400" />
                        <span className="font-medium">Reset Viewing Slots</span>
                        <span className="text-xs text-muted-foreground">Restore slots for a property</span>
                      </Button>
                      
                      <Button
                        variant="outline"
                        className="h-auto py-4 flex-col items-start border-white/20 hover:border-primary/50"
                        onClick={() => setControlDialog({ open: true, type: 'freeze', data: null })}
                        data-testid="control-freeze"
                      >
                        <PauseCircle className="w-5 h-5 mb-2 text-gold-400" />
                        <span className="font-medium">Freeze Listing</span>
                        <span className="text-xs text-muted-foreground">Temporarily disable a property</span>
                      </Button>
                      
                      <Button
                        variant="outline"
                        className="h-auto py-4 flex-col items-start border-white/20 hover:border-primary/50"
                        onClick={() => setControlDialog({ open: true, type: 'unfreeze', data: null })}
                        data-testid="control-unfreeze"
                      >
                        <PlayCircle className="w-5 h-5 mb-2 text-green-400" />
                        <span className="font-medium">Unfreeze Listing</span>
                        <span className="text-xs text-muted-foreground">Reactivate a frozen property</span>
                      </Button>
                      
                      <Button
                        variant="outline"
                        className="h-auto py-4 flex-col items-start border-white/20 hover:border-primary/50"
                        onClick={() => setControlDialog({ open: true, type: 'suspend-user', data: null })}
                        data-testid="control-suspend-user"
                      >
                        <Ban className="w-5 h-5 mb-2 text-red-400" />
                        <span className="font-medium">Suspend User</span>
                        <span className="text-xs text-muted-foreground">Block a user from the platform</span>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="alerts" className="mt-4">
                <RiskDashboard />
              </TabsContent>
            </Tabs>
          </div>

          {/* Live Activity Stream - Right Side */}
          <div className="col-span-4">
            <Card className="border-white/10 bg-card/50 sticky top-20" data-testid="activity-stream">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Activity className="w-5 h-5 text-green-400 animate-pulse" />
                    Live Activity Stream
                  </CardTitle>
                  <Badge variant="outline" className="border-green-600/50 text-green-400">
                    LIVE
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">Platform heartbeat - real-time events</p>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[calc(100vh-280px)]">
                  <div className="p-4 space-y-3">
                    {activityLogs.length === 0 ? (
                      <div className="text-center py-8">
                        <Activity className="w-8 h-8 text-muted-foreground/50 mx-auto mb-3" />
                        <p className="text-sm text-muted-foreground">No activity yet</p>
                        <p className="text-xs text-muted-foreground mt-1">Events will appear here in real-time</p>
                      </div>
                    ) : (
                      activityLogs.map((log, idx) => (
                        <ActivityLogItem 
                          key={idx} 
                          log={log} 
                          onClick={(l) => console.log('Log clicked:', l)}
                        />
                      ))
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Property Action Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => !open && setActionDialog({ open: false, type: null })}>
        <DialogContent className="bg-background border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {actionDialog.type === 'approve' ? (
                <CheckCircle2 className="w-5 h-5 text-green-400" />
              ) : (
                <XCircle className="w-5 h-5 text-red-400" />
              )}
              {actionDialog.type === 'approve' ? 'Approve Property' : 'Reject Property'}
            </DialogTitle>
            <DialogDescription>
              {actionDialog.type === 'approve' 
                ? 'This will activate the property. Seller will be notified.'
                : 'Property returns to draft. Seller will be notified.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <label className="text-sm font-medium mb-2 block">
              {actionDialog.type === 'approve' ? 'Approval Note (optional)' : 'Rejection Reason (required)'}
            </label>
            <Textarea
              placeholder={actionDialog.type === 'approve' 
                ? 'Add notes...' 
                : 'Explain why this property is being rejected...'}
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              className="bg-transparent border-white/20 min-h-[100px]"
              data-testid="action-reason-input"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, type: null })} className="border-white/20">
              Cancel
            </Button>
            <Button
              onClick={handlePropertyAction}
              disabled={processing || (actionDialog.type === 'reject' && !actionReason)}
              className={actionDialog.type === 'approve' 
                ? 'bg-green-600 hover:bg-green-700 text-white' 
                : 'bg-red-600 hover:bg-red-700 text-white'}
              data-testid="confirm-action-btn"
            >
              {processing ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Control Panel Dialog */}
      <Dialog open={controlDialog.open} onOpenChange={(open) => !open && setControlDialog({ open: false, type: null, data: null })}>
        <DialogContent className="bg-background border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-red-400" />
              Admin Control Action
            </DialogTitle>
            <DialogDescription>
              This action will be permanently logged. A reason is required.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {(controlDialog.type === 'reset-slots' || controlDialog.type === 'freeze' || controlDialog.type === 'unfreeze') && (
              <div>
                <label className="text-sm font-medium mb-2 block">Property ID</label>
                <Input
                  placeholder="Enter property ID"
                  value={controlDialog.data?.id || ''}
                  onChange={(e) => setControlDialog({ ...controlDialog, data: { id: e.target.value } })}
                  className="bg-transparent border-white/20"
                  data-testid="control-property-id"
                />
              </div>
            )}
            
            {controlDialog.type === 'suspend-user' && (
              <div>
                <label className="text-sm font-medium mb-2 block">User ID</label>
                <Input
                  placeholder="Enter user ID"
                  value={controlDialog.data?.id || ''}
                  onChange={(e) => setControlDialog({ ...controlDialog, data: { id: e.target.value } })}
                  className="bg-transparent border-white/20"
                  data-testid="control-user-id"
                />
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium mb-2 block">Reason (required)</label>
              <Textarea
                placeholder="Explain why this action is being taken..."
                value={controlReason}
                onChange={(e) => setControlReason(e.target.value)}
                className="bg-transparent border-white/20 min-h-[100px]"
                data-testid="control-reason-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setControlDialog({ open: false, type: null, data: null })} className="border-white/20">
              Cancel
            </Button>
            <Button
              onClick={handleControlAction}
              disabled={processing || !controlReason || controlReason.length < 5 || !controlDialog.data?.id}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="control-confirm-btn"
            >
              {processing ? 'Processing...' : 'Execute Action'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Detail Dialog */}
      <Dialog open={userDetailDialog} onOpenChange={setUserDetailDialog}>
        <DialogContent className="bg-background border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              User Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                  <User className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <p className="text-lg font-medium">{selectedUser.full_name || selectedUser.username}</p>
                  <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                </div>
              </div>
              
              <Separator className="bg-white/10" />
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Role</p>
                  <Badge className={
                    selectedUser.role === 'admin' ? 'bg-red-600/20 text-red-400 mt-1' :
                    selectedUser.role === 'seller' ? 'bg-blue-600/20 text-blue-400 mt-1' :
                    'bg-green-600/20 text-green-400 mt-1'
                  }>
                    {selectedUser.role}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">KYC Status</p>
                  <Badge className={
                    selectedUser.kyc_status === 'verified' ? 'bg-green-600/20 text-green-400 mt-1' :
                    'bg-gold-600/20 text-gold-400 mt-1'
                  }>
                    {selectedUser.kyc_status || 'pending'}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">User ID</p>
                  <p className="font-mono text-xs mt-1">{selectedUser.id}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Phone</p>
                  <p className="mt-1">{selectedUser.phone || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Username</p>
                  <p className="mt-1">{selectedUser.username || 'Not set'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Created</p>
                  <p className="mt-1">{selectedUser.created_at ? formatTimestamp(selectedUser.created_at) : 'N/A'}</p>
                </div>
                {selectedUser.role === 'seller' && (
                  <>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Seller Status</p>
                      <Badge className={
                        selectedUser.seller_status === 'active' ? 'bg-green-600/20 text-green-400 mt-1' :
                        'bg-gold-600/20 text-gold-400 mt-1'
                      }>
                        {selectedUser.seller_status || 'pending'}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Company</p>
                      <p className="mt-1">{selectedUser.company_name || 'Individual'}</p>
                    </div>
                  </>
                )}
              </div>
              
              <Separator className="bg-white/10" />
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-gold-600/50 text-gold-400 hover:bg-gold-950/30"
                  onClick={() => {
                    setUserDetailDialog(false);
                    setControlDialog({ open: true, type: 'suspend-user', data: { id: selectedUser.id } });
                  }}
                >
                  <Ban className="w-4 h-4 mr-1" />
                  Suspend User
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setActiveTab('overview')}
                >
                  View Activity
                </Button>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDetailDialog(false)} className="border-white/20">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
